import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Flex, Input, Text, useToast } from '@chakra-ui/react';
import useSWR from 'swr';
import baseService from '../../services/base';
import { fetchData } from '../../services/fetch';
import useDebouncedSave from '../../hooks/useDebouncedSave';
import SaveStatusHint from '../Feedback/SaveStatusHint';
import { AUDIT, AUDIT_LABEL } from '../../theme/auditTokens';

// undefined = valor inválido (não é número ou é negativo).
function parseScore(value) {
 const trimmed = String(value ?? '').trim();
 if (trimmed === '') return null;
 const parsed = Number(trimmed);
 if (Number.isNaN(parsed) || parsed < 0) return undefined;
 return parsed;
}

function toDisplay(score) {
 return score !== null && score !== undefined ? String(score) : '';
}

/**
 * Nota do auditor para uma resposta.
 *
 * Reescrito porque o valor "sumia" ao sair e voltar pra tela: a versão
 * anterior recebia o score como prop derivada da query gigante que monta o
 * questionário inteiro (`survey/respondent/company`), inicializava um
 * `useState(prop)` e nunca se recuperava se o SWR do pai mostrasse cache
 * antigo antes de revalidar — como o `key` do card não muda entre essas duas
 * renderizações, o React reaproveita a mesma instância e o campo ficava
 * travado no valor da primeira leitura (às vezes vazio).
 *
 * Agora o campo busca o próprio valor direto (`GET question-response/:id`,
 * endpoint pequeno e dedicado, independente do resto do questionário) e
 * atualiza o próprio cache local com a resposta confirmada do servidor
 * assim que salva — nada aqui depende de outra tela ter revalidado a tempo.
 */
export default function QuestionScoreField({ responseId, initialScore, isDisabled }) {
 const toast = useToast();
 const [score, setScore] = useState(toDisplay(initialScore));
 // true assim que o usuário mexe no campo — depois disso paramos de
 // ressincronizar do servidor pra não apagar o que ele está digitando.
 const hasLocalEditRef = useRef(false);
 const savedScoreRef = useRef(
  initialScore !== null && initialScore !== undefined ? Number(initialScore) : null
 );

 const { data: current, mutate } = useSWR(
  responseId ? `question-response/${responseId}` : null,
  fetchData,
  { fallbackData: initialScore !== undefined ? { score: initialScore } : undefined }
 );

 // Toda vez que o servidor confirma um valor (leitura inicial ou depois de um
 // save), refletimos no campo — a menos que o usuário já tenha começado a
 // editar (aí a edição local manda, até ele salvar ou sair da tela).
 useEffect(() => {
  if (hasLocalEditRef.current || current === undefined) return;
  const serverScore = current?.score ?? null;
  setScore(toDisplay(serverScore));
  savedScoreRef.current = serverScore !== null ? Number(serverScore) : null;
 }, [current]);

 const persistScore = async (value) => {
  const parsed = parseScore(value);
  if (parsed === undefined || parsed === savedScoreRef.current) return;
  const response = await baseService.patch(`question-response/${responseId}/score`, {
   score: parsed,
  });
  savedScoreRef.current = parsed;
  // Cache local vira a resposta CONFIRMADA pelo servidor — se o usuário sair
  // e voltar sem passar por outra tela, o valor já está certo sem esperar
  // nenhuma revalidação.
  mutate(response.data, { revalidate: false });
 };

 const scoreSave = useDebouncedSave(persistScore);

 function handleChange(value) {
  hasLocalEditRef.current = true;
  setScore(value);
  if (isDisabled) return;
  if (parseScore(value) === undefined) {
   scoreSave.cancel();
   return;
  }
  scoreSave.schedule(value);
 }

 function handleBlur() {
  if (isDisabled) return;
  if (parseScore(score) === undefined) {
   toast({ title: 'Nota inválida', status: 'warning' });
   return;
  }
  scoreSave.flush();
 }

 // Botão explícito, sem depender de debounce/blur/timing — reagenda o valor
 // atual e força o envio na hora. Complementa o auto-save, não o substitui.
 function handleSaveClick() {
  if (isDisabled) return;
  if (parseScore(score) === undefined) {
   toast({ title: 'Nota inválida', status: 'warning' });
   return;
  }
  scoreSave.schedule(score);
  scoreSave.flush();
 }

 if (!responseId) return null;

 return (
  <Box borderTop={`1px solid ${AUDIT.line}`} pt='14px' mt='14px'>
   <Flex align='center' gap='12px' wrap='wrap'>
    <Text sx={AUDIT_LABEL}>Nota da questão</Text>
    <Input
     type='number'
     min={0}
     value={score}
     onChange={(e) => handleChange(e.target.value)}
     onBlur={handleBlur}
     onKeyDown={(e) => {
      if (e.key === 'Enter') e.target.blur();
     }}
     isInvalid={parseScore(score) === undefined}
     isDisabled={isDisabled}
     placeholder='—'
     size='sm'
     w='110px'
     textAlign='center'
     bg={AUDIT.surface}
     borderColor={AUDIT.lineStrong}
    />
    {!isDisabled && (
     <Button
      size='sm'
      variant='outline'
      onClick={handleSaveClick}
      isLoading={scoreSave.status === 'saving'}
      color={AUDIT.accentInk}
      borderColor={AUDIT.accent}
      _hover={{ bg: AUDIT.accentSoft }}
     >
      Salvar
     </Button>
    )}
    {!isDisabled && (
     <SaveStatusHint status={scoreSave.status} onRetry={scoreSave.retry} />
    )}
   </Flex>
  </Box>
 );
}
